import { useId } from 'react';
import { Alert, Button } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { useDealerSecretSettingPanel } from '../hooks/useDealerSecretSettingPanel';
import { SecretSettingStatus } from './DealerSecretSettingPanel/SecretSettingStatus';
import { SecretSettingForm } from './DealerSecretSettingPanel/SecretSettingForm';

interface Props {
    dealerId: string;
    settingKey: string;
    label: string;
}

export function DealerSecretSettingPanel({ dealerId, settingKey, label }: Props) {
    const panel = useDealerSecretSettingPanel(dealerId, settingKey);
    const { status, isLoading, isError, formOpen, confirmingRemove, busy, message } = panel;
    const regionId = useId();

    if (isLoading) return <p className="text-sm text-mp-muted">Loading {label}...</p>;

    const hasOwnKey = status?.own.has_value ?? false;

    return (
        <section className="space-y-4 max-w-md" aria-label={label}>
            <h3 className="text-lg font-semibold">{label}</h3>
            {isError || !status ? (
                <Alert variant="error">Could not load the current key status.</Alert>
            ) : (
                <SecretSettingStatus status={status} />
            )}

            <PermissionGuard permission={{ resource: 'dealer', action: 'config-setting.secret.edit' }}>
                {message && (
                    <Alert variant={message.kind} role={message.kind === 'success' ? 'status' : 'alert'}>
                        {message.text}
                    </Alert>
                )}

                <div ref={panel.triggerRef} className="flex items-center gap-2">
                    <Button
                        size="sm"
                        type="button"
                        onClick={panel.toggleForm}
                        ariaExpanded={formOpen}
                        ariaControls={regionId}
                        disabled={busy}
                    >
                        {hasOwnKey ? 'Replace key' : 'Set key'}
                    </Button>
                    {hasOwnKey && !confirmingRemove && (
                        <Button size="sm" variant="outline" type="button" onClick={panel.startRemove} disabled={busy}>
                            Remove
                        </Button>
                    )}
                </div>

                {confirmingRemove && (
                    <div className="space-y-3">
                        <p className="text-sm">
                            Remove this dealer&apos;s own key? Third-party agreement search falls back to the
                            group&apos;s key, if it has one.
                        </p>
                        <div ref={panel.confirmRef} className="flex items-center gap-2">
                            <Button size="sm" variant="danger" type="button" onClick={panel.confirmRemove} disabled={busy}>
                                {panel.removing ? 'Removing...' : 'Confirm remove'}
                            </Button>
                            <Button size="sm" variant="outline" type="button" onClick={panel.cancelRemove} disabled={busy}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}

                <div id={regionId} hidden={!formOpen}>
                    {formOpen && (
                        <SecretSettingForm
                            key={panel.formVersion}
                            inputId={`${regionId}-secret`}
                            label={label}
                            submitLabel={hasOwnKey ? 'Replace key' : 'Save key'}
                            pending={panel.saving}
                            disabled={busy}
                            fieldError={panel.fieldError}
                            focusOnMount={panel.saveFailed}
                            onSubmit={panel.submit}
                            onCancel={panel.closeForm}
                        />
                    )}
                </div>
            </PermissionGuard>
        </section>
    );
}
